import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { GitBranch, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Section, ErrorBox, Empty, RevisionSelect, errMsg, fmtDate } from "./shared";

const SKIP = new Set(["ingredients", "id", "created_at", "updated_at", "user_id", "current_revision_id"]);

function nameMap(rev) {
  return Object.fromEntries((rev?.ingredients || []).map((i) => [i.id, i.name]));
}
function show(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function diffRevisions(a, b) {
  const an = nameMap(a), bn = nameMap(b);
  const ag = Object.fromEntries((a?.formula?.ingredients || []).map((r) => [r.ingredient_id, Number(r.grams)]));
  const bg = Object.fromEntries((b?.formula?.ingredients || []).map((r) => [r.ingredient_id, Number(r.grams)]));
  const ids = Array.from(new Set([...Object.keys(ag), ...Object.keys(bg)]));
  const ingredients = ids.map((id) => {
    const from = ag[id], to = bg[id];
    const kind = from == null ? "added" : to == null ? "removed" : from !== to ? "changed" : "same";
    return { id, name: bn[id] || an[id] || id, from, to, kind };
  }).sort((x, y) => (x.kind === "same") - (y.kind === "same") || x.name.localeCompare(y.name));
  const keys = Array.from(new Set([...Object.keys(a?.formula || {}), ...Object.keys(b?.formula || {})])).filter((k) => !SKIP.has(k));
  const fields = keys
    .filter((k) => JSON.stringify(a?.formula?.[k] ?? null) !== JSON.stringify(b?.formula?.[k] ?? null))
    .map((k) => ({ key: k, from: a?.formula?.[k], to: b?.formula?.[k] }));
  return { ingredients, fields };
}

const KIND = {
  added: "bg-emerald-50 text-emerald-800",
  removed: "bg-red-50 text-red-800 line-through",
  changed: "bg-amber-50 text-amber-900",
  same: "text-stone-500",
};

export default function RevisionsPanel({ recipeId, recipe, revisions, onReload }) {
  const [fromId, setFromId] = useState(revisions[1]?.id || revisions[0]?.id || "");
  const [toId, setToId] = useState(revisions[0]?.id || "");
  const [restoring, setRestoring] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [restoredOk, setRestoredOk] = useState(false);

  const a = revisions.find((r) => r.id === fromId);
  const b = revisions.find((r) => r.id === toId);
  const diff = useMemo(() => (a && b ? diffRevisions(a, b) : null), [a, b]);

  const doRestore = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/rd/recipes/${recipeId}/revisions/${restoring.id}/restore`, { expected_updated_at: recipe.updated_at });
      toast.success(`Rev ${restoring.number} restored as a new revision`);
      setRestoring(null); setRestoredOk(true);
      onReload();
    } catch (err) {
      setError(errMsg(err)); setRestoring(null);
    } finally { setBusy(false); }
  };

  return (
    <Section icon={GitBranch} title="Saved formula history" testId="rd-revisions"
      subtitle="Every save creates an immutable revision. Restoring copies an old revision forward as the newest one; nothing newer is deleted.">
      <ErrorBox message={error} testId="error-restore" />
      {restoredOk && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="text-restore-success">
          Restored. <Link to={`/builder/${recipeId}`} className="underline font-semibold" data-testid="link-open-builder-restored">Open in Builder</Link> to continue editing.
        </div>
      )}
      {revisions.length === 0 ? (
        <Empty>No saved revisions yet. Save the formula in the <Link to={`/builder/${recipeId}`} className="underline">Builder</Link> to start a traceable history.</Empty>
      ) : (
        <>
          <div className="divide-y divide-stone-100 border border-stone-200 rounded-md mb-5">
            {revisions.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm flex-wrap" data-testid={`row-revision-${r.id}`}>
                <div className="min-w-0">
                  <span className="font-mono text-xs font-bold text-stone-700 mr-2">Rev {r.number}</span>
                  {r.id === recipe.current_revision_id && <span className="text-[10px] text-[#17324D] font-bold mr-2">CURRENT</span>}
                  <span className="text-stone-500 text-xs">{fmtDate(r.created_at)}</span>
                  {r.reason && <span className="text-stone-700 ml-2">— {r.reason}</span>}
                  <span className="text-stone-400 text-xs ml-2">{(r.formula?.ingredients || []).length} ingredients</span>
                </div>
                {r.id !== recipe.current_revision_id && (
                  <Button size="sm" variant="outline" onClick={() => setRestoring(r)} data-testid={`button-restore-${r.id}`}>
                    <ArrowCounterClockwise size={13} className="mr-1" /> Restore
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-2">Compare revisions</div>
          <div className="grid sm:grid-cols-2 gap-2 mb-3">
            <RevisionSelect revisions={revisions} value={fromId} onChange={setFromId} testId="select-compare-from" />
            <RevisionSelect revisions={revisions} value={toId} onChange={setToId} testId="select-compare-to" />
          </div>
          {diff && fromId === toId && <p className="text-sm text-stone-500">Pick two different revisions to compare.</p>}
          {diff && fromId !== toId && (
            <div className="space-y-4" data-testid="compare-result">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-stone-400 text-left">
                      <th className="pb-1">Ingredient</th>
                      <th className="pb-1 text-right">Rev {a.number} (g)</th>
                      <th className="pb-1 text-right">Rev {b.number} (g)</th>
                      <th className="pb-1 text-right">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.ingredients.map((d) => (
                      <tr key={d.id} className={`border-t border-stone-100 ${KIND[d.kind]}`}>
                        <td className="py-1.5 px-1">{d.name}</td>
                        <td className="py-1.5 text-right font-mono">{d.from == null ? "—" : d.from.toFixed(2)}</td>
                        <td className="py-1.5 text-right font-mono">{d.to == null ? "—" : d.to.toFixed(2)}</td>
                        <td className="py-1.5 text-right text-xs font-semibold px-1">
                          {d.kind === "changed" ? `${d.to - d.from > 0 ? "+" : ""}${(d.to - d.from).toFixed(2)} g` : d.kind === "same" ? "" : d.kind}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">Other changed fields</div>
                {diff.fields.length === 0 ? (
                  <p className="text-sm text-stone-500">No other recipe fields differ.</p>
                ) : (
                  <dl className="space-y-1.5">
                    {diff.fields.map((f) => (
                      <div key={f.key} className="text-sm grid sm:grid-cols-[180px_1fr] gap-1">
                        <dt className="font-mono text-xs text-stone-500">{f.key}</dt>
                        <dd className="text-stone-800 break-words"><span className="text-red-700">{show(f.from)}</span> <span className="text-stone-400">→</span> <span className="text-[#17324D]">{show(f.to)}</span></dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <AlertDialog open={!!restoring} onOpenChange={(o) => !o && setRestoring(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Rev {restoring?.number}?</AlertDialogTitle>
            <AlertDialogDescription>
              The formula from Rev {restoring?.number} will be saved as a new, newest revision. All newer revisions, test records, sensory data and decisions stay in history unchanged. Approvals do not carry over to the new revision.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-restore">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); doRestore(); }} data-testid="button-confirm-restore">
              {busy ? "Restoring…" : "Restore as new revision"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}
